/**
 * Значение вкладки (sessionStorage) с общей последней копией (localStorage) для новых вкладок.
 * Хелперы зовутся на каждый запрос, поэтому без `useStorage`: тот оставлял бы слушатель и watch
 * на каждый вызов. Недоступное хранилище (приватный режим) читается как пустое.
 */
function tabValue(key: string) {
  const read = (storage: Storage): string | null => {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  };
  const write = (value: string | null) => {
    for (const storage of [sessionStorage, localStorage]) {
      try {
        if (value === null) {
          storage.removeItem(key);
        } else {
          storage.setItem(key, value);
        }
      } catch {
        // Хранилище недоступно: значение живёт только в сторе.
      }
    }
  };

  return { get: () => read(sessionStorage) ?? read(localStorage), set: write };
}

/**
 * Origin-scoped tenant storage used by request providers and tenant transitions.
 * У каждой вкладки своя локация; новая вкладка берёт последнюю выбранную.
 */
export function useLocationStorage() {
  const value = tabValue('last-location-id');

  return {
    getLocationId: value.get,
    setLocationId: (id: string | null) => value.set(id),
    clearLocationId: () => value.set(null),
  };
}

export function useOrganizationStorage() {
  const value = tabValue('organization-id');

  return {
    getOrganizationId: value.get,
    setOrganizationId: (organizationId: string | null) => value.set(organizationId),
    resetOrganizationId: () => value.set(null),
  };
}

const organizationLocationKey = (organizationId: string) => `last-location-id:${organizationId}`;

/** Последняя локация организации: после смены организации открывается она. */
export function rememberOrganizationLocation(organizationId: string, locationId: string): void {
  try {
    localStorage.setItem(organizationLocationKey(organizationId), locationId);
  } catch {
    // Хранилище недоступно: запоминать нечего.
  }
}

export function rememberedOrganizationLocation(organizationId: string): string | null {
  try {
    return localStorage.getItem(organizationLocationKey(organizationId));
  } catch {
    return null;
  }
}
